/**
 * Timeline keyboard shortcuts (SPEC "UI / UX Priorities" — fast posing).
 *
 * Active only in ANIMATE mode and only when the keystroke is not going into a text field.
 * Undo/redo (Ctrl+Z / Ctrl+Y) are deliberately NOT handled here — the shell owns them.
 */

import type { EditorContext } from '../context';
import { allKeyFrames, boneKeyFrames, nextKeyFrame, prevKeyFrame } from './frames';

/** Frames stepped by Shift + arrow. */
const BIG_STEP = 10;

function isTextTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || tag === 'OPTION';
}

/**
 * Install the window-level handler. `sheet` is the dope-sheet element — `A` (select all keys of
 * the selected bone) only fires while focus is inside it, so it cannot fight other panels.
 */
export function installShortcuts(ctx: EditorContext, sheet: HTMLElement): () => void {
  const store = ctx.store;

  function onKeyDown(event: KeyboardEvent): void {
    const state = store.getState();
    if (state.mode !== 'animate') return;
    if (isTextTarget(event.target)) return;

    const mod = event.ctrlKey || event.metaKey;
    const key = event.key;
    const lower = key.length === 1 ? key.toLowerCase() : key;

    // The shell owns history shortcuts.
    if (mod && (lower === 'z' || lower === 'y')) return;

    const playhead = Math.round(state.playhead.frame);
    const duration = state.clip?.durationFrames ?? 0;
    const step = (delta: number): void => {
      event.preventDefault();
      store.actions.setPlayhead(playhead + delta);
    };

    if (mod) {
      if (lower === 'c') {
        event.preventDefault();
        store.actions.copyKeys();
      } else if (lower === 'v') {
        event.preventDefault();
        store.actions.pasteKeys(playhead);
      }
      return;
    }

    switch (key) {
      case ' ':
      case 'Spacebar':
        event.preventDefault();
        store.actions.togglePlay();
        return;
      case 'Delete':
      case 'Backspace':
        event.preventDefault();
        // An event marker and a keyframe selection are separate selections, so Delete has to pick
        // one. The marker wins when it is selected, because selecting one is a deliberate click on
        // the band while a key selection often lingers from earlier work.
        if (state.selectedEvent) store.actions.deleteSelectedEvent();
        else store.actions.deleteSelectedKeys();
        return;
      case 'Home':
        event.preventDefault();
        store.actions.setPlayhead(0);
        return;
      case 'End':
        event.preventDefault();
        store.actions.setPlayhead(duration);
        return;
      case 'ArrowLeft':
        step(event.shiftKey ? -BIG_STEP : -1);
        return;
      case 'ArrowRight':
        step(event.shiftKey ? BIG_STEP : 1);
        return;
      default:
        break;
    }

    switch (lower) {
      case 'k': {
        event.preventDefault();
        if (event.shiftKey) {
          store.actions.keyAllPosed();
        } else if (state.selection.bone) {
          store.actions.keyBone(state.selection.bone);
        } else {
          // Same fallback as the transport's Key button: never a silent no-op.
          store.actions.keyAllPosed();
        }
        return;
      }
      case ',': {
        const target = prevKeyFrame(allKeyFrames(state.clip), playhead);
        if (target === null) return;
        event.preventDefault();
        store.actions.setPlayhead(target);
        return;
      }
      case '.': {
        const target = nextKeyFrame(allKeyFrames(state.clip), playhead);
        if (target === null) return;
        event.preventDefault();
        store.actions.setPlayhead(target);
        return;
      }
      case 'l':
        event.preventDefault();
        store.actions.setLoop(!state.playhead.loop);
        return;
      case 'a': {
        if (!sheet.contains(document.activeElement)) return;
        const bone = state.selection.bone;
        if (!bone) return;
        event.preventDefault();
        store.actions.selectKeys(
          boneKeyFrames(state.clip, bone).map((frame) => ({ bone, frame })),
        );
        return;
      }
      default:
        break;
    }
  }

  window.addEventListener('keydown', onKeyDown);
  return () => window.removeEventListener('keydown', onKeyDown);
}
